/**
 * SummaryContent Component
 * Unified deployment summary UI with three collapsible sections
 * Used by both Dashboard (multi-tenant) and Tenant (single-tenant) SummaryStep components
 */

import React, { forwardRef } from 'react';
import { Text, Spinner, Button, tokens } from '@fluentui/react-components';
import {
  Shield20Regular,
  DocumentBulletList20Regular,
} from '@fluentui/react-icons';
import { CollapsibleSection } from './CollapsibleSection';
import { SitLabelSummary } from './SitLabelSummary';
import type { SummaryEntity } from '../utils';
import './summary.css';

interface RuleSummaryItem {
  id: string;
  name: string;
  /** Optional tenant label prefix. When defined, a blue prefix pill is shown beneath the name (dashboard/multi-tenant only). */
  prefix?: string;
  rules: Array<{ ruleId: string; ruleName: string }>;
}

interface SummaryContentProps {
  /** Array of items (tenants or policies) with their rules to deploy */
  ruleSummaries: RuleSummaryItem[];
  /** Total count of rules across all items */
  totalRulesCount: number;
  /** SITs already present in the tenant */
  sitsExisting: SummaryEntity[];
  /** SITs that will be created */
  sitsNew: SummaryEntity[];
  /** Labels already present in the tenant */
  labelsExisting: SummaryEntity[];
  /** Labels that will be created */
  labelsNew: SummaryEntity[];
  /** Intro text to display at the top */
  introText?: string;
  /** Optional note rendered just above Section 3 ("Items that will be created"), e.g. label-prefix info */
  labelPrefixNote?: React.ReactNode;
  /** Optional header content to show in screenshot (e.g., policy name, timestamp) */
  screenshotHeader?: React.ReactNode;
  /** When true, sections 2 and 3 show a loading state while the latest deployment
   * status is being re-fetched from the target tenant(s).
   *
   * Both sections are gated together on purpose: they are the two halves of a single
   * partition of the selected SITs/Labels (existing vs. new) produced by one pass over
   * the same data. Showing one while the other loads would surface a stale
   * "already present in the tenant" list next to a fresh "will be created" list. */
  isRefreshing?: boolean;
  /** Set when the refresh FAILED. Sections 2 and 3 then show data that was never revalidated,
   * so the caller must keep Confirm disabled; this renders the explanation and retry. */
  refreshError?: string | null;
  /** Invoked by the "Try again" affordance shown alongside {@link refreshError}. */
  onRetryRefresh?: () => void;
  /** Number of target tenants being deployed to. Used only to phrase the refresh copy
   * correctly — this component is shared by the single-tenant dialog and the
   * multi-tenant dashboard wizard. Defaults to 1. */
  targetTenantCount?: number;
}

/**
 * Renders the three-section deployment summary UI:
 * 1. Rules to be deployed
 * 2. Items already present in tenant
 * 3. Items to be created during deployment
 * 
 * Uses forwardRef to allow parent components to capture screenshots
 */
export const SummaryContent = forwardRef<HTMLDivElement, SummaryContentProps>(({
  ruleSummaries,
  totalRulesCount,
  sitsExisting,
  sitsNew,
  labelsExisting,
  labelsNew,
  introText = 'Below is a summary of the rules that will be deployed to the target tenant, including the Sensitive Information Types (SITs) and Labels that are already present and those that will be created.',
  labelPrefixNote,
  screenshotHeader,
  isRefreshing = false,
  refreshError = null,
  onRetryRefresh,
  targetTenantCount = 1,
}, ref) => {
  // Shared blue info banner explaining prefixing. Rendered once at the very top of the
  // modal, so the labels-and-policies note is visible up front.
  const prefixBanner = labelPrefixNote ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        margin: '0 0 20px',
        padding: '10px 12px',
        background: '#eff6fc',
        border: '1px solid #0078d4',
        borderRadius: 6,
      }}
    >
      <DocumentBulletList20Regular style={{ color: '#0078d4', flexShrink: 0, marginTop: 2 }} />
      <Text style={{ fontSize: 13, color: '#004578', lineHeight: 1.5 }}>
        {labelPrefixNote}
      </Text>
    </div>
  ) : null;

  const isMultiTenant = targetTenantCount > 1;
  const tenantScopeText = isMultiTenant
    ? `all ${targetTenantCount} selected tenants`
    : 'the tenant';

  // Shared loading state for sections 2 and 3, which are the two halves of one
  // existing/new partition and must never be shown in mixed freshness.
  const refreshingNotice = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0 8px' }}>
      <Spinner size="tiny" />
      <Text style={{ fontSize: 13, color: '#444' }}>
        {isMultiTenant
          ? `Checking the latest deployment status in the ${targetTenantCount} target tenants\u2026`
          : 'Checking the latest deployment status in the target tenant\u2026'}
      </Text>
    </div>
  );

  // Shown in place of sections 2 and 3 when the refresh failed. The lists are NOT revalidated,
  // so they are hidden rather than presented as current, and the caller keeps Confirm disabled
  // until a retry succeeds.
  const refreshFailedNotice = (
    <div style={{ padding: '4px 0 8px' }}>
      <Text style={{ fontSize: 13, color: tokens.colorStatusDangerForeground1, display: 'block', marginBottom: 8 }}>
        Could not check the latest deployment status
        {isMultiTenant ? ` in the ${targetTenantCount} target tenants` : ' in the target tenant'}
        {refreshError ? `: ${refreshError}` : '.'}{' '}
        The list is hidden because it would be out of date.
      </Text>
      {onRetryRefresh && (
        <Button size="small" appearance="secondary" onClick={onRetryRefresh}>
          Try again
        </Button>
      )}
    </div>
  );

  /** Sections 2 and 3 are two halves of one partition, so they always share a single state. */
  const renderSection = (content: React.ReactNode) =>
    isRefreshing ? refreshingNotice : refreshError ? refreshFailedNotice : content;

  return (
    <div ref={ref} className="summary-content">
      {/* Screenshot header - only visible in screenshot capture */}
      {screenshotHeader && (
        <div className="summary-screenshot-header">
          {screenshotHeader}
        </div>
      )}
      
      {/* Intro Heading */}
      <Text className="summary-intro-text">
        {introText}
      </Text>

      {/* Prefix info banner, shown up front at the start of the modal */}
      {prefixBanner}

      {/* Section 1: Rules to be deployed */}
      <CollapsibleSection
        title={`1. Rules to be deployed (${totalRulesCount})`}
        icon={<Shield20Regular style={{ color: '#0078d4' }} />}
      >
        <div className="summary-grid-three-col">
          {ruleSummaries.map(({ id, name, prefix, rules }) => (
            <div key={id}>
              <Text className="summary-section-label">
                {name} ({rules.length} rule{rules.length !== 1 ? 's' : ''})
              </Text>
              {prefix !== undefined && (
                <div style={{ marginTop: 4, marginBottom: 6 }}>
                  <span
                    style={{
                      padding: '1px 6px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 700,
                      background: '#eff6fc',
                      color: '#0078d4',
                      border: '1px solid #0078d4',
                      whiteSpace: 'nowrap',
                    }}
                    title="Parent and standalone labels are deployed with this prefix; child labels are not prefixed."
                  >
                    Prefix: {prefix || '—'}
                  </span>
                </div>
              )}
              <ul className="summary-list">
                {rules.map(rule => (
                  <li key={rule.ruleId} className="summary-list-item">
                    <Shield20Regular style={{ fontSize: 14, color: '#0078d4', flexShrink: 0 }} />
                    <span>{rule.ruleName}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Section 2: Items already present in the target tenant(s) */}
      <CollapsibleSection
        title={isMultiTenant ? '2. Items already present in all selected tenants' : '2. Items already present in the tenant'}
        icon={<DocumentBulletList20Regular style={{ color: '#666' }} />}
        variant="neutral"
      >
        {renderSection(
          <>
            <Text style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 14, lineHeight: 1.5 }}>
              These SITs ({sitsExisting.length}) and Labels ({labelsExisting.length}) are already available in {tenantScopeText} and will not be modified.
            </Text>
            <SitLabelSummary
              sitsLabel="SITs already in tenant"
              labelsLabel="Labels already in tenant"
              sits={sitsExisting}
              labels={labelsExisting}
              variant="existing"
            />
          </>
        )}
      </CollapsibleSection>

      {/* Section 3: Items that will be created during deployment */}
      <CollapsibleSection
        title="3. Items that will be created during deployment"
        icon={<DocumentBulletList20Regular style={{ color: '#0078d4' }} />}
        variant="create"
      >
        {renderSection(
          <>
            <Text style={{ fontSize: 13, color: '#444', display: 'block', marginBottom: 14, lineHeight: 1.5 }}>
              These SITs ({sitsNew.length}) and Labels ({labelsNew.length}) {isMultiTenant
                ? 'are missing from at least one selected tenant and will be created where needed.'
                : 'do not exist in the tenant and will be created as part of deployment.'}
            </Text>
            <SitLabelSummary
              sitsLabel="SITs to be created"
              labelsLabel="Labels to be created"
              sits={sitsNew}
              labels={labelsNew}
              variant="new"
            />
          </>
        )}
      </CollapsibleSection>
    </div>
  );
});

SummaryContent.displayName = 'SummaryContent';

export default SummaryContent;
